'use client';

import { useState, useEffect } from 'react';
import { HslColorPicker, HslColor } from 'react-colorful';
import { Input } from '@/components/ui/input';

interface ColorPickerSimpleProps {
  value?: string; // HSL string like "246 72% 65%"
  onChange?: (value: string) => void;
  className?: string;
}

// Convert HSL string "246 72% 65%" to HslColor object { h: 246, s: 72, l: 65 }
const hslStringToHslColor = (hslString: string): HslColor => {
  if (!hslString || typeof hslString !== 'string') {
    return { h: 0, s: 0, l: 50 };
  }
  
  try {
    const parts = hslString.trim().split(/\s+/);
    if (parts.length === 3) {
      const h = parseFloat(parts[0]) || 0;
      const s = parseFloat(parts[1].replace('%', '')) || 0;
      const l = parseFloat(parts[2].replace('%', '')) || 50;
      
      return {
        h: Math.max(0, Math.min(360, h)),
        s: Math.max(0, Math.min(100, s)),
        l: Math.max(0, Math.min(100, l))
      };
    }
  } catch (error) {
    console.error('Error parsing HSL string:', error);
  }
  
  return { h: 0, s: 0, l: 50 };
};

// Convert HslColor object to string "246 72% 65%"
const hslColorToString = (hsl: HslColor): string => {
  return `${Math.round(hsl.h)} ${Math.round(hsl.s)}% ${Math.round(hsl.l)}%`;
};

// Convert HslColor to hex for display
const hslColorToHex = (hsl: HslColor): string => {
  const h = hsl.h;
  const s = hsl.s / 100;
  const l = hsl.l / 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  
  let r = 0, g = 0, b = 0;
  
  if (h >= 0 && h < 60) {
    r = c; g = x; b = 0;
  } else if (h >= 60 && h < 120) {
    r = x; g = c; b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0; g = c; b = x;
  } else if (h >= 180 && h < 240) {
    r = 0; g = x; b = c;
  } else if (h >= 240 && h < 300) {
    r = x; g = 0; b = c;
  } else {
    r = c; g = 0; b = x;
  }
  
  const toHex = (n: number) => {
    const hex = Math.round((n + m) * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const ColorPickerSimple = ({
  value = '0 0% 50%',
  onChange,
  className,
}: ColorPickerSimpleProps) => {
  const [hslColor, setHslColor] = useState<HslColor>(() => hslStringToHslColor(value));

  // Update internal state when external value changes
  useEffect(() => {
    const newHslColor = hslStringToHslColor(value);
    setHslColor(newHslColor);
  }, [value]);

  const handleColorChange = (newHsl: HslColor) => {
    setHslColor(newHsl);
    const hslString = hslColorToString(newHsl);
    onChange?.(hslString);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    onChange?.(inputValue);
  };

  const hexValue = hslColorToHex(hslColor);
  const hslString = hslColorToString(hslColor);

  return (
    <div className={className}>
      <div className="flex flex-col gap-4 p-4">
        {/* Color Picker */}
        <div className="w-full">
          <HslColorPicker 
            color={hslColor} 
            onChange={handleColorChange}
            style={{ width: '100%', height: '200px' }}
          />
        </div>
        
        {/* Color Info Display */}
        <div className="flex flex-col gap-2">
          {/* HEX Display */}
          <div className="flex items-center gap-2">
            <div 
              className="h-10 w-10 rounded border border-input"
              style={{ backgroundColor: hexValue }}
            />
            <div className="flex-1">
              <Input
                value={hexValue}
                readOnly
                className="font-mono text-sm"
              />
            </div>
          </div>
          
          {/* HSL Value Input */}
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">
              HSL Value:
            </label>
            <Input
              value={hslString}
              onChange={handleInputChange}
              placeholder="246 72% 65%"
              className="font-mono text-sm"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
